import { getCurrencySymbol, toFixedTrunc } from '../../../client/Juno/helpers';
import { DECIMAL_PLACES, ErrorTransactions } from '../../helpers/constant';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CopyButton from '../common/CopyButton';
import JncPaymentConfirmation from '../profile/JncPaymentConfirmation';
// Memoized field renderer to prevent unnecessary re-renders
const renderFieldRows = (fields, details) => {
    return Object.entries(fields).map(([label, fieldOrValue]) => {
        const value =
            typeof fieldOrValue === 'string' && details.hasOwnProperty(fieldOrValue)
                ? details[fieldOrValue]
                : fieldOrValue;

        return (
            <div key={label} className="p-3">
                <div className="text-table-col-title text-xs uppercase font-semibold mb-1">
                    {label}
                </div>
                <div className="flex items-center">
                    <span className="!text-text break-words">
                        {typeof value === 'object' && value !== null
                            ? JSON.stringify(value, null, DECIMAL_PLACES.FIAT)
                            : value?.toString() || '—'}
                    </span>
                    {value && (
                        <div className="ml-1">
                            <CopyButton type="button" copyValue={value} />
                        </div>
                    )}
                </div>
            </div>
        );
    });
};

// Helper functions for common calculations
const formatCurrency = (amount, currency) =>
    `${getCurrencySymbol(currency)} ${toFixedTrunc(amount, DECIMAL_PLACES.FIAT)}`;

const calculateBalanceBeforeDeduction = (amount, transactionFee) =>
    transactionFee ? Number(amount) + Number(transactionFee) : amount;

const calculateBalanceAfterDeduction = (amount, transactionFee) =>
    transactionFee ? Number(amount) - Number(transactionFee) : amount;

// Field builders for each transaction type
const buildWithdrawalFields = (row, details, transactionFee) => {
    const balanceBeforeDeduction = calculateBalanceBeforeDeduction(
        details?.amount,
        row?.transactionFee,
    );

    return {
        'Transaction Id': row.transactionId,
        Currency: details?.currency || '-',
        [transactionFee ? 'Amount before deduction' : 'Amount']:
            balanceBeforeDeduction && details.currency
                ? formatCurrency(balanceBeforeDeduction, details.currency)
                : '-',
        ...(transactionFee && {
            'Amount after deduction':
                details.amount && details.currency
                    ? formatCurrency(details.amount, details.currency)
                    : '-',
        }),
        'Transaction Fee': formatCurrency(transactionFee || 0, row.currency) || '-',
        'Balance after transaction':
            row?.balanceAfterTransaction && details.currency
                ? formatCurrency(row.balanceAfterTransaction, details.currency)
                : '-',
        'Beneficiary name': details?.beneficiaryName || '-',
        'Beneficiary address': details?.beneficiaryAddress || '-',
        'Beneficiary country': details?.beneficiaryCountry || '-',
        'Bank name': details?.bankName || '-',
        'Beneficiary account number': details?.beneficiaryAccountNumber || '-',
        'Bank address': details?.bankAddress || '-',
        'Bank country': details?.bankCountry || '-',
        IBAN: details?.iban || '-',
        SWIFT: details?.swift || '-',
        'Sort code': details?.sortCode || '-',
        Reference: details?.reference || '-',
    };
};

const buildDepositFields = (row, details, transactionFee) => {
    const balanceAfterDeduction = calculateBalanceAfterDeduction(
        details?.amount,
        row?.transactionFee,
    );

    return {
        'Transaction Id': row.transactionId,
        Currency: details?.currency || '-',
        [transactionFee ? 'Amount before deduction' : 'Amount']:
            details.amount && details.currency
                ? formatCurrency(details.amount, details.currency)
                : '-',
        ...(transactionFee && {
            'Amount after deduction':
                balanceAfterDeduction && details.currency
                    ? formatCurrency(balanceAfterDeduction, details.currency)
                    : '-',
        }),
        'Transaction Fee': formatCurrency(transactionFee || 0, row.currency) || '-',
        'Balance after transaction':
            row?.balanceAfterTransaction && details.currency
                ? formatCurrency(row.balanceAfterTransaction, details.currency)
                : '-',
        'Remitter name': details.remitterName || '-',
        Reference: details.reference || '-',
    };
};

const buildTransferFields = (row, details, transactionFee) => {
    const balanceBeforeDeduction = calculateBalanceBeforeDeduction(
        details?.amount,
        row?.transactionFee,
    );

    return {
        'Transaction Id': row.transactionId,
        ...(transactionFee && {
            'Amount before deduction':
                balanceBeforeDeduction && details?.currency
                    ? formatCurrency(balanceBeforeDeduction, details.currency)
                    : '-',
        }),
        [transactionFee ? 'Amount after deduction' : 'Amount']:
            details.amount && details.currency
                ? formatCurrency(details.amount, details.currency)
                : '-',
        ...(row?.transferInOut === 'Out' && {
            'Transaction Fee': formatCurrency(transactionFee || 0, row?.currency) || '-',
        }),
        ...(row?.transferInFee && {
            'Merchant Fee':
                row.transferInFee && row.currency
                    ? formatCurrency(row.transferInFee, row.currency)
                    : '-',
        }),
        'Balance after transaction':
            row?.balanceAfterTransaction && details.currency
                ? formatCurrency(row.balanceAfterTransaction, details.currency)
                : '-',
        'Juno Money Email': details?.email || '',
        'Juno Money Account Name': details?.accountName || '-',
        Reference: details?.reference || '-',
    };
};

const buildFXFields = (row, details) => ({
    'Transaction Id': row.transactionId,
    'Currency exchanged from': details?.fromCurrency || '-',
    'Currency exchanged to': details?.toCurrency || '-',
    'FX rates': toFixedTrunc(details?.fxrate, DECIMAL_PLACES.FX_RATE) || '-',
    'Amount (exchanged from currency)':
        details?.fromAmount && details.fromCurrency
            ? formatCurrency(details.fromAmount, details.fromCurrency)
            : '-',
    'Amount (exchanged to currency)':
        details?.toAmount && details.toCurrency
            ? formatCurrency(details.toAmount, details.toCurrency)
            : '-',
    'Balance after transaction':
        row?.balanceAfterTransaction && details.fromCurrency
            ? formatCurrency(row.balanceAfterTransaction, details.fromCurrency)
            : '-',
});

const buildReceiveFields = (row, details) => ({
    'From Wallet Address': details.fromWalletAddress,
    'To Wallet Address': details.toWalletAddress,
    'Crypto Amount': `${getCurrencySymbol(details.cryptoId)} ${toFixedTrunc(
        details.cryptoAmount,
        DECIMAL_PLACES.FX_RATE,
    )}`,
    'Balance After Transaction': `${details.cryptoId} ${toFixedTrunc(
        row.balanceAfterTransaction,
        DECIMAL_PLACES.CRYPTO,
    )}`,
    'Total Amount': `${getCurrencySymbol(details.cryptoId)} ${details.totalAmount}`,
});

const buildExchangeFields = (row, details) => ({
    'From Crypto Amount': `${getCurrencySymbol(details.fromCryptoId)} ${details.fromCryptoAmount}`,
    'To Crypto Amount': `${getCurrencySymbol(details.toCryptoId)} ${toFixedTrunc(
        details.toCryptoAmount,
        details.toCryptoAmount < DECIMAL_PLACES.CRYPTO_THRESHOLD
            ? DECIMAL_PLACES.CRYPTO
            : DECIMAL_PLACES.FIAT,
    )}`,
    'Balance After Transaction': `${details.fromCryptoId} ${toFixedTrunc(
        row.balanceAfterTransaction,
        DECIMAL_PLACES.CRYPTO,
    )}`,
    'Exchange Rate': `1 ${details.fromCryptoId} = ${details.toCryptoId} ${details?.quotationSide?.toLowerCase() == 'buy' ? (1 / Number(details.quotationPrice)).toFixed(DECIMAL_PLACES.CRYPTO) : Number(details.quotationPrice).toFixed(DECIMAL_PLACES.CRYPTO)}`,
});

const buildSendFields = (row, details) => ({
    'From Address': details.fromAddress,
    'To Address': details.toAddress,
    'Crypto Amount': `${getCurrencySymbol(details.cryptoId)} ${toFixedTrunc(
        details.cryptoAmount,
        DECIMAL_PLACES.CRYPTO,
    )}`,
    'Balance After Transaction': `${details.cryptoId} ${toFixedTrunc(
        row.balanceAfterTransaction,
        DECIMAL_PLACES.CRYPTO,
    )}`,
});

const buildBuyCryptoFields = (row, details) => {
    // FOR FIAT page, we will show BUY as Transfer Out, Hence showing the related fields only
    const activeCurrencyPage = localStorage.getItem('activeCurrencyPage');
    if (activeCurrencyPage === 'fiat') {
        return {
            'Transaction Id': row.transactionId,
            Amount: formatCurrency(details.fiatAmount, details.fiatCurrency),
            To: ' Juno Pay Ltd',
        };
    }
    return {
        'Crypto Amount': `${getCurrencySymbol(details.cryptoId)} ${details.cryptoAmount}`,
        'Fiat Currency': details.fiatCurrency,
        'Fiat Amount': `${getCurrencySymbol(details.fiatCurrency)} ${toFixedTrunc(
            details.fiatAmount,
            DECIMAL_PLACES.FIAT,
        )}`,
        'Total Amount': details.totalAmount,
        'Balance After Transaction': `${details.cryptoId} ${toFixedTrunc(
            row.balanceAfterTransaction,
            DECIMAL_PLACES.CRYPTO,
        )}`,
        'Exchange Rate': `1 ${details.cryptoId} = ${details.fiatCurrency} ${Number(details.quotationPrice).toFixed(DECIMAL_PLACES.CRYPTO)}`,
    };
};

const buildSellCryptoFields = (row, details) => {
    // FOR FIAT page, we will show SELL as Transfer In, Hence showing the related fields only
    const activeCurrencyPage = localStorage.getItem('activeCurrencyPage');
    if (activeCurrencyPage === 'fiat') {
        return {
            'Transaction Id': row.transactionId,
            Amount: formatCurrency(details.fiatAmount, details.fiatCurrency),
            From: ' Juno Pay Ltd',
        };
    }
    return {
        'Crypto Amount': `${getCurrencySymbol(details.cryptoId)} ${details.cryptoAmount}`,
        'Fiat Currency': details.fiatCurrency,
        'Fiat Amount': `${getCurrencySymbol(details.fiatCurrency)} ${toFixedTrunc(
            details.fiatAmount,
            DECIMAL_PLACES.FIAT,
        )}`,
        'Total Amount': details.totalAmount,
        'Exchange Rate': `1 ${details.cryptoId} = ${details.fiatCurrency} ${Number(details.quotationPrice).toFixed(DECIMAL_PLACES.CRYPTO)}`,
    };
};
const buildBulkPaymentFields = (row, details) => {
    const fields = {
        'Transaction ID': row.transactionId,
        'Total Transactions': row.bulkPaymentId?.transactions?.length || 0,
        'Total Documents': row.bulkPaymentId?.attachments?.length || 0,
    };

    // Add total amounts for each currency
    if (row.bulkPaymentId?.totalAmount) {
        row.bulkPaymentId.totalAmount.forEach(({ amount, currency }, index) => {
            fields[`${currency} Amount`] = `${getCurrencySymbol(
                currency,
            )} ${toFixedTrunc(amount, DECIMAL_PLACES.FIAT)}`;
        });
    }

    return fields;
};

const buildDefaultFields = details =>
    Object.fromEntries(Object.keys(details).map(key => [key.replace(/_/g, ' '), key]));

// Field builder factory
const getFieldBuilder = type => {
    const builders = {
        'Payment Out': buildWithdrawalFields,
        Withdrawal: buildWithdrawalFields,
        Deposit: buildDepositFields,
        Transfer: buildTransferFields,
        FX: buildFXFields,
        Receive: buildReceiveFields,
        Exchange: buildExchangeFields,
        Send: buildSendFields,
        Buy: buildBuyCryptoFields,
        Sell: buildSellCryptoFields,
        'Bulk Payment': buildBulkPaymentFields,
    };

    return builders[type] || buildDefaultFields;
};

const TransactionExpanded = ({ row }) => {
    const { type, transactionDetails, transactionFee, status } = row;

    // Early returns for invalid data
    if (!transactionDetails?.length && type !== 'Bulk Payment') return null;

    // Prepare transaction details
    const details = { ...transactionDetails[0] };

    // Build fields using factory pattern
    const fieldBuilder = getFieldBuilder(type);
    const fieldsToDisplay =
        fieldBuilder === buildDefaultFields
            ? fieldBuilder(details)
            : fieldBuilder(row, details, transactionFee);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-DECIMAL_PLACES.FIAT md:grid-cols-3 gap-2 text-sm bg-table-content-bg px-4 py-2">
            {ErrorTransactions.includes(status.code) && (
                <div className="p-3 col-span-full bg-red-400/10 rounded-md">
                    <ErrorOutlineIcon className="!text-[#F97066] mr-2" />
                    <span className="!text-[#F97066] text-sm font-semibold uppercase break-words ">
                        {status.message}
                    </span>
                </div>
            )}
            {renderFieldRows(fieldsToDisplay, row)}
            {status.code === 'PrivateClient:TransactionCompleted' &&
                ['Withdrawal', 'Payment Out'].includes(type) && (
                    <div className="p-3 col-span-full rounded-md">
                        <JncPaymentConfirmation data={row} />
                    </div>
                )}
        </div>
    );
};

export default TransactionExpanded;
